import { FC } from "react";

import { GetServerSideProps, InferGetServerSidePropsType as Infer } from "next";
import { useTranslation } from "next-i18next";

import { FullWidthVStack } from "@components/common";
import { ContextMessage } from "@components/Message";
import LayoutHead from "@components/PageHeader/LayoutHead";
import withPropsMiddleware from "@lib/withPropsMiddleware";
import { checkHasAuthCookie } from "@modules/auth";
import { Banners, loadBanners } from "@modules/banners";
import {loadFeatures, useFeatureData} from "@modules/features";
import Layout, { TWithLayout, LayoutContent } from "@modules/layout";
import Notifications, { INotifications, loadNotifications } from "@modules/notifications";
import { RegistrationForm } from "@modules/registration";
import { ROUTES } from "@utils/constants";
import getEnv from "@utils/getEnv";
import { IBanners } from "@modules/banners/models/banners";
import UserFeedbackForm from "@modules/userFeedback/components/UserFeedbackForm";
import { FEEDBACK_FORM } from "@modules/features/constants";
import { Desktop } from "@lib/media";

type TWithLocales = {
  defaultLocale: string;
  customLocales: string[];
  customLocale: string; // Change from string? to string
  features: string[] | null;
  banners: IBanners | null;
  notifications: INotifications | null;
};

export const getServerSideProps: GetServerSideProps<TWithLocales> = withPropsMiddleware(
  async ({
    locale,
    req: {
      headers: { host },
      cookies,
    },
  }) => {
    // Redirect to landing page if user is logged in
    if (checkHasAuthCookie(cookies)) {
      return {
        redirect: {
          permanent: false,
          destination: ROUTES.LANDING,
        },
      };
    }

    const bannersZone = getEnv("NEXT_PUBLIC_BANNERS_REGISTER_ZONE") as string;
    const bannersPromise = locale ? loadBanners(bannersZone, locale, host!) : null;

    const notificationsZone = getEnv("NEXT_PUBLIC_NOTIFICATION_REGISTER_ZONE") as string;
    const notificationsPromise = locale ? loadNotifications(notificationsZone, locale, host!) : null;
    const [banners, notifications] = await Promise.all([bannersPromise, notificationsPromise]);
    const features = locale ? await loadFeatures(locale, host) : null;

    return {
      props: { banners, notifications, features, defaultLocale: "", customLocales: [], customLocale: "" },
    };
  }
);

const RegisterPage: TWithLayout<FC<Infer<typeof getServerSideProps>>> = ({ banners, notifications }) => {
  const { t } = useTranslation();
  const features = useFeatureData();
  return (
    <>
      <LayoutHead title={t("pages-register-title")} description={t("pages-register-description")} />
      <LayoutContent>
        <FullWidthVStack spacing="1.5rem">
          <Banners bannersData={banners} isDesktopVisible isMobileVisible />
          <Notifications notificationsData={notifications} />
          <ContextMessage messageZone="register" />
          <RegistrationForm />
        </FullWidthVStack>
        { features?.includes(FEEDBACK_FORM) && <><Desktop><UserFeedbackForm /></Desktop></> }
      </LayoutContent>
    </>
  );
};

RegisterPage.Layout = <Layout hasRecaptcha hasWebchat={false} hasMobileWebchat={false} />;

export default RegisterPage;
